export interface SharePreviewCardProps {
  /** Absolute image URL. Omit to show the "no image" plate. */
  imageUrl?: string;
  title: string;
  description: string;
  /** Bare host the chat app prints under the card, e.g. `khalifa.trioty.com`. */
  host: string;
  /** Shown inside the image plate when there is no image. Must be translated. */
  emptyImageLabel: string;
  /** Caption above the bubble, e.g. "How your link looks in WhatsApp". */
  caption?: string;
  className?: string;
}

/**
 * The seller's view of what a chat app shows for their link.
 *
 * Shaped like an outgoing WhatsApp bubble on purpose — a seller recognises that
 * shape instantly, and the whole point of the panel is "this is what the person
 * you paste it to will see", not "here are three fields you filled in".
 *
 * Fed by `resolveStoreShareCard` / `resolveProductShareCard`, the same
 * functions the storefront's `og:*` tags come from, so the preview cannot
 * promise a card the buyer app would not emit. Purely presentational: the
 * green is the chat bubble's, not the portal's, because imitating WhatsApp is
 * the message.
 */
export function SharePreviewCard({
  imageUrl,
  title,
  description,
  host,
  emptyImageLabel,
  caption,
  className = "",
}: SharePreviewCardProps) {
  return (
    <div className={["flex flex-col gap-2", className].filter(Boolean).join(" ")}>
      {caption ? (
        <div className="text-[11px] font-medium text-[color:var(--text-muted)]">
          {caption}
        </div>
      ) : null}

      {/* Chat pane. `dir="ltr"` — a link preview is laid out the same way in
          both languages, and the URL line is always LTR. */}
      <div
        dir="ltr"
        className="flex justify-end rounded-2xl bg-[#ECE5DD] p-3 dark:bg-[#0b141a]"
      >
        <div className="w-full max-w-[280px] rounded-xl bg-[#DCF8C6] p-1 shadow-[0_1px_1px_rgba(0,0,0,0.13)] dark:bg-[#005c4b]">
          {/* Preview block — image, then title, then one description line. */}
          <div className="overflow-hidden rounded-lg bg-[rgba(0,0,0,0.05)]">
            <div className="relative w-full overflow-hidden bg-[rgba(0,0,0,0.08)] pt-[52.35%]">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <span className="absolute inset-0 flex items-center justify-center px-2 text-center text-[11px] text-[color:var(--text-muted)]">
                  {emptyImageLabel}
                </span>
              )}
            </div>
            <div className="flex flex-col gap-0.5 px-2 py-1.5">
              <div className="line-clamp-2 text-[12.5px] font-semibold leading-[1.3] text-[#111b21] dark:text-white">
                {title}
              </div>
              <div className="line-clamp-1 text-[11.5px] leading-[1.35] text-[rgba(17,27,33,0.6)] dark:text-[rgba(255,255,255,0.6)]">
                {description}
              </div>
              <div className="truncate text-[11px] uppercase text-[rgba(17,27,33,0.45)] dark:text-[rgba(255,255,255,0.45)]">
                {host}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
